import React, { useState, DragEvent, useRef } from 'react';
import { Stack, Typography, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestoreIcon from '@mui/icons-material/Restore';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { Image } from 'antd';
import styles from './photoManagement.module.css';

type PhotoItem = {
  id: number;
  url: string;
  storage: {
    fileName: string;
    type: string;
  };
};

type PhotoManagementProps = {
  disabled?: boolean;
  existingPhotos: PhotoItem[];
  newPhotos: File[];
  deletedPhotoIds: number[];
  onAddPhotos: (files: File[]) => void;
  onDeleteExistingPhoto: (photoId: number) => void;
  onRemoveNewPhoto: (index: number) => void;
  onRestorePhoto: (photoId: number) => void;
  /** When set, used as section title instead of "Photo Management". Use for e.g. "Issue Photos Before". */
  sectionLabel?: string;
  /** When true, hides the large dropzone and uses inline 80x80 upload squares instead. */
  compactUpload?: boolean;
};

export const PhotoManagement = ({
  disabled,
  existingPhotos,
  newPhotos,
  deletedPhotoIds,
  onAddPhotos,
  onDeleteExistingPhoto,
  onRemoveNewPhoto,
  onRestorePhoto,
  sectionLabel,
  compactUpload
}: PhotoManagementProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const files = event.target.files;
    if (files && files.length > 0) {
      const photoFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));
      if (photoFiles.length > 0) {
        onAddPhotos(photoFiles);
      } else {
        alert('Please select only image files');
      }
    }
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const photoFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));
      if (photoFiles.length > 0) {
        onAddPhotos(photoFiles);
      } else {
        alert('Please drop only image files');
      }
    }
  };

  const getPhotoPreviewUrl = (file: File): string => {
    return URL.createObjectURL(file);
  };

  const activeExistingPhotos = existingPhotos.filter(
    (photo) => !deletedPhotoIds.includes(photo.id)
  );

  const deletedExistingPhotos = existingPhotos.filter((photo) =>
    deletedPhotoIds.includes(photo.id)
  );

  return (
    <Stack width="100%" gap={2}>
      {sectionLabel ? (
        <Typography variant="h6" color="primary" fontWeight={600}>
          {sectionLabel}
        </Typography>
      ) : (
        <Typography variant="h6" color="primary" fontWeight={600}>
          Photo Management
        </Typography>
      )}

      {/* Upper row: existing + new photos (+ optional inline upload square) */}
      <Stack direction="row" flexWrap="wrap" gap={2} alignItems="flex-start">
        {activeExistingPhotos.length > 0 && (
          <Box>
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {activeExistingPhotos.map((photo) => (
                <Box
                  key={photo.id}
                  sx={{
                    position: 'relative',
                    width: 80,
                    height: 80,
                    border: '1px solid #ddd',
                    borderRadius: 1,
                    overflow: 'hidden',
                    cursor: 'pointer'
                  }}>
                  <Image src={photo.url} alt={photo.storage.fileName} className={styles.photo} />
                  <IconButton
                    onClick={() => {
                      if (disabled) return;
                      onDeleteExistingPhoto(photo.id);
                    }}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -2,
                      right: -2,
                      bgcolor: 'transparent',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' }
                    }}>
                    <CloseIcon fontSize="small" color="error" />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* New Photos to Upload */}
        {newPhotos.length > 0 && (
          <Box>
            <Typography variant="subtitle2" mb={1} color="success.main">
              New Photos to Upload ({newPhotos.length})
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {newPhotos.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    width: 80,
                    height: 80,
                    border: '2px solid #4caf50',
                    borderRadius: 1,
                    overflow: 'hidden',
                    cursor: 'pointer'
                  }}>
                  <Image
                    src={getPhotoPreviewUrl(file)}
                    alt={file.name}
                    style={{
                      width: 80,
                      height: 80
                    }}
                  />
                  <IconButton
                    onClick={() => {
                      if (disabled) return;
                      onRemoveNewPhoto(index);
                    }}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' }
                    }}>
                    <CloseIcon fontSize="small" color="error" />
                  </IconButton>
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      bgcolor: 'rgba(76, 175, 80, 0.8)',
                      color: 'white',
                      fontSize: '10px',
                      textAlign: 'center',
                      py: 0.25
                    }}>
                    NEW
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* Inline upload square (compact mode) */}
        {!disabled && compactUpload && (
          <Box
            onClick={() => fileInputRef.current?.click()}
            sx={{
              position: 'relative',
              width: 80,
              height: 80,
              border: '2px dashed #ccc',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              bgcolor: '#f8f9fa',
              '&:hover': {
                bgcolor: '#e3f2fd',
                borderColor: '#1976d2'
              }
            }}>
            <AddPhotoAlternateIcon sx={{ color: '#888', fontSize: 24 }} />
          </Box>
        )}
      </Stack>

      {/* Upload Area (hidden in compact mode, uses inline square instead) */}
      {!compactUpload && (
        <Box>
          <Typography variant="subtitle2" mb={1}>
            Add New Photos
          </Typography>
          <Box
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            sx={{
              width: '100%',
              height: 120,
              border: '2px dashed #ccc',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              borderRadius: '8px',
              bgcolor: isDragging ? '#e3f2fd' : '#f8f9fa',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: '#e3f2fd',
                borderColor: '#1976d2'
              }
            }}>
            <AddPhotoAlternateIcon
              sx={{ color: isDragging ? '#1976d2' : '#888', fontSize: 32, mb: 1 }}
            />
            <Typography variant="body2" color="textSecondary" textAlign="center">
              {isDragging ? 'Drop photos here' : 'Click or drag photos here'}
            </Typography>
          </Box>
          {newPhotos.length > 0 && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
              New Photos to Upload ({newPhotos.length})
            </Typography>
          )}
        </Box>
      )}
      {/* Hidden file input (used by both layouts) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Deleted Photos (for undo) */}
      {deletedExistingPhotos.length > 0 && (
        <Box>
          <Typography variant="subtitle2" mb={1} color="error.main">
            Photos to Delete ({deletedExistingPhotos.length})
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={2}>
            {deletedExistingPhotos.map((photo) => (
              <Box
                key={photo.id}
                sx={{
                  position: 'relative',
                  width: 80,
                  height: 80,
                  border: '2px solid #f44336',
                  borderRadius: 1,
                  overflow: 'hidden',
                  opacity: 0.6
                }}>
                <Image
                  src={photo.url}
                  alt={photo.storage.fileName}
                  style={{
                    width: 80,
                    height: 80,
                    filter: 'grayscale(100%)'
                  }}
                />
                <IconButton
                  onClick={() => {
                    if (disabled) return;
                    onRestorePhoto(photo.id);
                  }}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' }
                  }}>
                  <RestoreIcon fontSize="small" color="success" />
                </IconButton>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    bgcolor: 'rgba(244, 67, 54, 0.8)',
                    color: 'white',
                    fontSize: '10px',
                    textAlign: 'center',
                    py: 0.25
                  }}>
                  DELETE
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Summary */}
      {(newPhotos.length > 0 || deletedPhotoIds.length > 0) && (
        <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
          <Typography variant="body2" fontWeight={600} mb={1}>
            Changes Summary:
          </Typography>
          {newPhotos.length > 0 && (
            <Typography variant="body2" color="success.main">
              • {newPhotos.length} new photo(s) will be uploaded
            </Typography>
          )}
          {deletedPhotoIds.length > 0 && (
            <Typography variant="body2" color="error.main">
              • {deletedPhotoIds.length} existing photo(s) will be deleted
            </Typography>
          )}
        </Box>
      )}
    </Stack>
  );
};
